import { BorderButton } from "src/components/buttons/BorderButton";
import StudentAge from "src/components/items/StudentAge";
import UserModel from "src/models/UserModel";
import { modals, useModal } from "src/utils/modals";
import { UsaSwimmingAccount, UsaSwimmingParticipant } from "types/usa-swimming";
import Modal from "./Modal";

interface Props {
  user?: UserModel;
  account: UsaSwimmingAccount;
  participant?: UsaSwimmingParticipant & { account: UsaSwimmingAccount };
  importAsNew: ({ accountId, participantId }) => void;
  importAsMatched: ({ accountId, participantId }) => void;
}

export const ReviewModal = modals.create((props: Props) => {
  const modal = useModal();

  function onCancel() {
    modal.hide();
  }

  function importAsNew() {
    props.importAsNew({
      accountId: props.account.id,
      participantId: props.participant?.id,
    });

    onCancel();
  }

  function importAsMatched() {
    props.importAsMatched({
      accountId: props.account.id,
      participantId: props.participant?.id,
    });

    onCancel();
  }

  const isAccount = props.account && !props.participant;
  const isParticipant = props.participant && props.account;

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      title="Review"
      handleCancel={onCancel}
      footer={
        <>
          <BorderButton bgColor="#fff" onClick={importAsMatched}>
            <i className="far fa-merge text-organization" /> Yes
          </BorderButton>
          <BorderButton bgColor="#fff" onClick={importAsNew}>
            <i className="far fa-xmark text-organization" /> No
          </BorderButton>
          <BorderButton onClick={onCancel} type="reset">
            Not sure yet
          </BorderButton>
        </>
      }
    >
      <p className="text-sm">
        <i className="far fa-arrow-right-arrow-left text-organization" />{" "}
        Matched by name, email, or phone
      </p>

      <div className="mb-4">
        <p className="step-subhead">
          USA Swimming {!props.participant ? "Account" : "Member"}
        </p>

        {isAccount && (
          <>
            <strong>
              {props.account.firstName} {props.account.lastName}
            </strong>
            <div className="mb-1 text-sm">{props.account.email}</div>
            <div className="text-sm">
              <i className="far fa-user text-organization" />{" "}
              {props.account.participants
                .map((p) => `${p.firstName} ${p.lastName}`)
                .join(", ")}
            </div>
          </>
        )}

        {isParticipant && (
          <>
            <strong>
              {props.participant.firstName} {props.participant.lastName}
            </strong>
            <div className="text-sm">
              <StudentAge student={props.participant} />
              {props.participant.gender && ` • ${props.participant.gender}`}
            </div>
            <div className="text-sm">
              <i className="far fa-users-gear text-organization" />{" "}
              {props.account.email}
            </div>
          </>
        )}
      </div>

      <div className="mb-4">
        <p className="step-subhead">
          Existing Captyn {props.account ? "Account" : "Participant"}
        </p>

        {isAccount && props.account.matched && (
          <>
            <strong>
              {props.account.matched.firstName} {props.account.matched.lastName}
            </strong>
            <div className="mb-1 text-sm">{props.account.matched.email}</div>
            {props.account.matched.participants.length > 0 && (
              <div className="text-sm">
                <i className="far fa-user text-organization" />{" "}
                {props.account.matched.participants
                  .map((p) => `${p.firstName} ${p.lastName}`)
                  .join(", ")}
              </div>
            )}
          </>
        )}

        {isParticipant && props.participant.matched && (
          <>
            <strong>
              {props.participant.matched.firstName}{" "}
              {props.participant.matched.lastName}
            </strong>
            <div className="mb-1 text-sm">
              <StudentAge student={props.participant.matched} />
              {props.participant.matched.gender &&
                ` • ${props.participant.matched.gender}`}
            </div>
            <div className="text-sm">
              <i className="far fa-users-gear text-organization" />{" "}
              {props.account.matched.email}
            </div>
          </>
        )}
      </div>

      <p>
        <strong>Are these the same {isAccount ? "account" : "person"}?</strong>
      </p>
    </Modal>
  );
});
